import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { assetUrl } from '../../utils/photoslopApi';

function pillClass(active) {
  return `inline-flex shrink-0 items-center rounded-full px-3 py-1 font-['VT323'] text-[14px] transition ${
    active
      ? 'bg-[#00FF9F] text-black shadow-sm'
      : 'border border-black/10 bg-white/90 text-black/50 hover:text-black/70'
  }`;
}

function AssetTile({ asset, selected, slotNum, onClick }) {
  const src = assetUrl(asset.path || asset.thumbnail || asset.s3Url);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative aspect-square w-full overflow-hidden rounded-xl border-2 bg-white transition ${
        selected ? 'border-[#00FF9F] ring-2 ring-[#00FF9F]/30' : 'border-black/5 hover:border-black/15'
      }`}
    >
      <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
      {selected && slotNum != null ? (
        <span className="absolute left-1 top-1 rounded-md bg-[#00FF9F] px-1.5 py-0.5 font-['VT323'] text-xs text-black">
          @{slotNum}
        </span>
      ) : null}
    </button>
  );
}

function OnboardingSteps({ step }) {
  const steps = [
    { n: 1, text: 'Choose a spice — a bucket of reference images' },
    { n: 2, text: 'Tap one image → it becomes @1 (your root)' },
    { n: 3, text: 'Add more from other spices → @2, @3…' },
    { n: 4, text: 'Describe the scene, hit Create' },
  ];
  return (
    <div className="space-y-2 rounded-xl border border-black/5 bg-white/80 p-3">
      <p className="font-['VT323'] text-[15px] text-black/55">How it works</p>
      {steps.map((s) => (
        <div
          key={s.n}
          className={`flex gap-2 font-['VT323'] text-[14px] leading-snug ${
            step >= s.n ? 'text-black/70' : 'text-black/25'
          }`}
        >
          <span
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${
              step >= s.n ? 'bg-[#00FF9F] text-black' : 'bg-black/5 text-black/30'
            }`}
          >
            {s.n}
          </span>
          <span>{s.text}</span>
        </div>
      ))}
    </div>
  );
}

export default function PhotoslopLibrary({
  spices,
  library,
  activeFolderKey,
  root,
  secondaries,
  pickingRoot,
  loading,
  loadError,
  onActiveFolderChange,
  onPickRoot,
  onToggleSecondary,
  onUpload,
  onRefreshSpices,
  onCreateSpice,
  onClose,
}) {
  const fileRef = useRef(null);
  const [query, setQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const folders = spices?.folders ?? [];
  const assetsByFolder = spices?.assetsByFolder ?? {};
  const generated = library?.generated ?? [];

  const activeKey = activeFolderKey || folders.find((f) => f.count > 0)?.key || folders[0]?.key || 'yours';
  const isYoursTab = activeKey === 'yours';

  const filteredFolders = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return folders;
    return folders.filter(
      (f) => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q),
    );
  }, [folders, query]);

  const activeSpice = folders.find((f) => f.key === activeKey);

  const assets = useMemo(() => {
    if (isYoursTab) {
      return generated.map((g) => ({
        path: g.path,
        source: 'yours',
        filename: g.filename,
      }));
    }
    return (assetsByFolder[activeKey] || []).map((a) => ({
      ...a,
      source: activeKey,
    }));
  }, [isYoursTab, generated, assetsByFolder, activeKey]);

  const onboardingStep = root ? (secondaries.length ? 4 : 3) : pickingRoot ? 2 : 1;

  const slotForPath = (path) => {
    if (root?.path === path) return 1;
    const idx = secondaries.findIndex((s) => s.path === path);
    if (idx >= 0) return idx + 2;
    return null;
  };

  const handleTileClick = (asset) => {
    const selected = { path: asset.path, source: asset.source || activeKey };
    if (pickingRoot || !root) {
      onPickRoot(selected);
      toast.success('Root set (@1) — add more refs or describe your scene', { duration: 3500 });
      return;
    }
    if (selected.path === root.path) return;
    onToggleSecondary(selected);
    const n = secondaries.length + 2;
    toast(`Added @${n}`, { icon: '✦', duration: 2000 });
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || isYoursTab) return;
    setUploading(true);
    try {
      await onUpload(activeKey, file);
      toast.success('Added to spice');
      await onRefreshSpices();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#F8F7F4]">
      <div className="flex flex-shrink-0 items-start justify-between gap-2 border-b border-black/5 px-3 py-3">
        <div>
          <h2 className="font-['VT323'] text-[22px] text-black/85">Reference images</h2>
          <p className="font-['VT323'] text-[13px] leading-snug text-black/40">
            {pickingRoot || !root
              ? 'Tap one → becomes @1'
              : 'Tap more for @2, @3… then close and write your prompt'}
          </p>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-black/10 bg-white px-3 py-1.5 font-['VT323'] text-[16px] text-black/55 hover:bg-black/5"
            aria-label="Close spice panel"
          >
            Done
          </button>
        ) : null}
      </div>

      <div className="flex-shrink-0 space-y-2 border-b border-black/5 px-3 py-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search spices…"
          className="w-full rounded-lg border border-black/10 bg-white px-2 py-1.5 font-['VT323'] text-[15px] text-black/70 placeholder:text-black/25 focus:border-[#00FF9F]/50 focus:outline-none focus:ring-1 focus:ring-[#00FF9F]/30"
        />
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
          {filteredFolders.slice(0, 40).map((f) => (
            <button
              key={f.key}
              type="button"
              className={pillClass(activeKey === f.key)}
              onClick={() => onActiveFolderChange(f.key)}
            >
              {f.label}
              <span className="ml-1 opacity-60">{f.count}</span>
            </button>
          ))}
          {filteredFolders.length > 40 ? (
            <span className="self-center px-1 font-['VT323'] text-[12px] text-black/30">
              +{filteredFolders.length - 40} more — search
            </span>
          ) : null}
          <button
            type="button"
            className={pillClass(isYoursTab)}
            onClick={() => onActiveFolderChange('yours')}
          >
            Yours
            <span className="ml-1 opacity-60">{generated.length}</span>
          </button>
        </div>
        {!isYoursTab ? (
          <div className="flex gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={handleFile}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="flex-1 rounded-lg border border-dashed border-black/15 bg-white/90 py-1.5 font-['VT323'] text-[14px] text-black/55 hover:border-[#00FF9F]/40 hover:text-black/75 disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : `+ Add image to ${activeSpice?.label || 'spice'}`}
            </button>
            <button
              type="button"
              onClick={async () => {
                const name = window.prompt('New spice name (e.g. goldeyes, my-event)');
                if (!name?.trim()) return;
                try {
                  await onCreateSpice(name.trim());
                  toast.success('Spice created');
                  await onRefreshSpices();
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : String(err));
                }
              }}
              className="rounded-lg border border-black/10 bg-white/90 px-2 py-1.5 font-['VT323'] text-[14px] text-black/45 hover:text-black/65"
              title="New spice folder"
            >
              +
            </button>
          </div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex flex-col items-center gap-2 py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#00FF9F] border-t-transparent" />
            <p className="font-['VT323'] text-[14px] text-black/35">Loading spices…</p>
          </div>
        ) : loadError ? (
          <div className="space-y-3">
            <p className="rounded-lg border border-[#FF1493]/25 bg-[#FF1493]/8 px-3 py-2 font-['VT323'] text-[14px] text-black/70">
              {loadError}
            </p>
            <button
              type="button"
              onClick={onRefreshSpices}
              className="w-full rounded-lg bg-black/5 py-2 font-['VT323'] text-[15px] hover:bg-black/10"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="font-['VT323'] text-[14px] text-black/40 hover:text-black/60"
            >
              How it works
            </button>
            {helpOpen ? <OnboardingSteps step={1} /> : null}
          </div>
        ) : assets.length === 0 ? (
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setHelpOpen((o) => !o)}
              className="font-['VT323'] text-[14px] text-black/40"
            >
              {helpOpen ? '▾' : '▸'} How it works
            </button>
            {helpOpen ? <OnboardingSteps step={onboardingStep} /> : null}
            {isYoursTab ? (
              <p className="text-center font-['VT323'] text-[15px] leading-snug text-black/35">
                Creations you generate land here.
                <br />
                Start from a <strong className="text-black/50">spice tab</strong> above — e.g. Robin or
                Goldeyes.
              </p>
            ) : (
              <p className="text-center font-['VT323'] text-[15px] leading-snug text-black/35">
                No images in this spice yet.
                <br />
                Use <strong className="text-black/50">+ Add image</strong> above.
              </p>
            )}
          </div>
        ) : (
          <>
            {!root ? (
              <button
                type="button"
                onClick={() => setHelpOpen((o) => !o)}
                className="mb-2 font-['VT323'] text-[14px] text-black/35"
              >
                {helpOpen ? '▾' : '▸'} How it works
              </button>
            ) : null}
            {helpOpen && !root ? <OnboardingSteps step={onboardingStep} /> : null}
            <motion.div layout className="grid grid-cols-2 gap-2">
              {assets.map((asset) => {
                const path = asset.path;
                const slot = slotForPath(path);
                return (
                  <AssetTile
                    key={path}
                    asset={asset}
                    selected={slot != null}
                    slotNum={slot}
                    onClick={() => handleTileClick(asset)}
                  />
                );
              })}
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
